import { useCallback, useEffect, useRef, useState } from "react";
import type { GasStationResponseDto } from "@pro-sto/api-client";
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from "../constants";
import { createGasStationMarkerIcon } from "../gasStationMarker";
import {
  expandMapBounds,
  filterStationsInMapBounds,
} from "../mapViewport";
import {
  loadLeafletCluster,
  type LeafletWithClusterLib,
  type MarkerClusterGroup,
} from "../loadLeafletCluster";

interface GasStationsMapProps {
  stations: GasStationResponseDto[];
  selectedUuid: string | null;
  isVisible: boolean;
  onSelectStation: (uuid: string) => void;
}

interface LLatLng {
  lat: number;
  lng: number;
}

interface LMarker {
  getLatLng(): LLatLng;
  setLatLng(latlng: [number, number]): LMarker;
  on(event: string, handler: () => void): LMarker;
}

interface ReadableMapBounds {
  getNorth(): number;
  getSouth(): number;
  getEast(): number;
  getWest(): number;
}

interface LMap {
  setView(latlng: [number, number], zoom: number): LMap;
  getBounds(): ReadableMapBounds;
  invalidateSize(): LMap;
  on(event: string, handler: () => void): LMap;
  off(event: string, handler: () => void): LMap;
  remove(): void;
}

interface MarkerState {
  marker: LMarker;
  status: GasStationResponseDto["status"];
  selected: boolean;
}

const TILE_URL =
  "https://tile2.maps.2gis.com/tiles?x={x}&y={y}&z={z}&v=1&r=g&ts=online_sd";
const DEFAULT_CENTER: [number, number] = [
  DEFAULT_MAP_CENTER.lat,
  DEFAULT_MAP_CENTER.lon,
];
const SELECTED_ZOOM = 15;

function toCoord(value: number | string | undefined): number | null {
  if (value == null) return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getStationCoords(
  station: GasStationResponseDto,
): [number, number] | null {
  const lat = toCoord(station.lat);
  const lon = toCoord(station.lon);
  if (lat == null || lon == null) return null;
  return [lat, lon];
}

function getStationsForViewport(
  allStations: GasStationResponseDto[],
  map: LMap,
  selectedUuid: string | null,
): GasStationResponseDto[] {
  const bounds = expandMapBounds(map.getBounds());
  const visible = filterStationsInMapBounds(
    allStations,
    bounds,
    getStationCoords,
  );

  if (!selectedUuid) {
    return visible;
  }

  if (visible.some((station) => station.uuid === selectedUuid)) {
    return visible;
  }

  const selected = allStations.find((station) => station.uuid === selectedUuid);
  return selected ? [...visible, selected] : visible;
}

const GasStationsMap: React.FC<GasStationsMapProps> = ({
  stations,
  selectedUuid,
  isVisible,
  onSelectStation,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LMap | null>(null);
  const clusterGroupRef = useRef<MarkerClusterGroup | null>(null);
  const markersRef = useRef<Map<string, MarkerState>>(new Map());
  const leafletRef = useRef<LeafletWithClusterLib | null>(null);
  const onSelectRef = useRef(onSelectStation);
  const selectedUuidRef = useRef(selectedUuid);
  const stationsRef = useRef(stations);
  const syncVisibleMarkersRef = useRef<() => void>(() => undefined);
  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  onSelectRef.current = onSelectStation;
  stationsRef.current = stations;
  selectedUuidRef.current = selectedUuid;

  const createMarker = useCallback(
    (station: GasStationResponseDto, isSelected: boolean) => {
      const L = leafletRef.current;
      const coords = getStationCoords(station);
      if (!L || !coords) return null;

      const marker = L.marker(coords, {
        icon: createGasStationMarkerIcon(L, station.status, isSelected),
      }) as LMarker;

      marker.on("click", () => {
        onSelectRef.current(station.uuid);
      });

      return marker;
    },
    [],
  );

  const syncVisibleMarkers = useCallback(() => {
    const map = mapRef.current;
    const clusterGroup = clusterGroupRef.current;
    if (!map || !clusterGroup) return;

    const activeSelectedUuid = selectedUuidRef.current;
    const stationsInView = getStationsForViewport(
      stationsRef.current,
      map,
      activeSelectedUuid,
    );
    const nextVisibleIds = new Set(stationsInView.map((station) => station.uuid));
    const nextMarkers = new Map<string, MarkerState>();

    for (const [uuid, state] of markersRef.current) {
      if (!nextVisibleIds.has(uuid)) {
        clusterGroup.removeLayer(state.marker);
        continue;
      }
      nextMarkers.set(uuid, state);
    }

    for (const station of stationsInView) {
      const coords = getStationCoords(station);
      if (!coords) continue;

      const isSelected = station.uuid === activeSelectedUuid;
      const existing = nextMarkers.get(station.uuid);

      if (
        existing &&
        existing.status === station.status &&
        existing.selected === isSelected
      ) {
        existing.marker.setLatLng(coords);
        continue;
      }

      if (existing) {
        clusterGroup.removeLayer(existing.marker);
      }

      const marker = createMarker(station, isSelected);
      if (!marker) continue;

      clusterGroup.addLayer(marker);
      nextMarkers.set(station.uuid, {
        marker,
        status: station.status,
        selected: isSelected,
      });
    }

    markersRef.current = nextMarkers;
  }, [createMarker]);

  syncVisibleMarkersRef.current = syncVisibleMarkers;

  const handleViewportChange = useCallback(() => {
    syncVisibleMarkersRef.current();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;

    loadLeafletCluster()
      .then((L) => {
        if (cancelled || !containerRef.current) return;

        leafletRef.current = L;
        const map = L.map(containerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_MAP_ZOOM,
          maxZoom: 18,
          attributionControl: false,
        }) as LMap;

        L.tileLayer(TILE_URL, {
          maxZoom: 18,
          maxNativeZoom: 18,
          tms: false,
          attribution:
            '&copy; <a href="https://2gis.ru" target="_blank" rel="noopener noreferrer">2GIS</a>',
        }).addTo(map);

        const clusterGroup = L.markerClusterGroup({
          showCoverageOnHover: false,
          spiderfyOnMaxZoom: true,
          zoomToBoundsOnClick: true,
          maxClusterRadius: 64,
          removeOutsideVisibleBounds: true,
          animate: true,
        });

        clusterGroup.addTo(map);
        clusterGroupRef.current = clusterGroup;
        mapRef.current = map;

        map.on("moveend", handleViewportChange);
        map.on("zoomend", handleViewportChange);

        setMapReady(true);
        setTimeout(() => {
          map.invalidateSize();
          syncVisibleMarkersRef.current();
        }, 300);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
      setMapReady(false);
      markersRef.current.clear();
      mapRef.current?.off("moveend", handleViewportChange);
      mapRef.current?.off("zoomend", handleViewportChange);
      clusterGroupRef.current?.remove();
      clusterGroupRef.current = null;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      leafletRef.current = null;
    };
  }, [handleViewportChange]);

  useEffect(() => {
    if (!mapReady || !isVisible) return;
    syncVisibleMarkers();
    const timer = window.setTimeout(() => {
      mapRef.current?.invalidateSize();
      syncVisibleMarkers();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [stations, selectedUuid, mapReady, isVisible, syncVisibleMarkers]);

  useEffect(() => {
    if (!mapReady || !selectedUuid) return;
    const state = markersRef.current.get(selectedUuid);
    const map = mapRef.current;
    if (!state || !map) return;

    const pos = state.marker.getLatLng();
    map.setView([pos.lat, pos.lng], SELECTED_ZOOM);
  }, [selectedUuid, mapReady]);

  if (loadError) {
    return (
      <div className="gs-page__map gs-page__map--error">
        Не удалось загрузить карту
      </div>
    );
  }

  return <div ref={containerRef} className="gs-page__map" />;
};

GasStationsMap.displayName = "GasStationsMap";
export default GasStationsMap;
